'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { getApiUrl } from '@/lib/apiConfig';
import { authService } from '@/lib/authService';
import { fetchWithRetry } from '@/lib/fetchUtils';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import {
  getAiProposalManagerClientSettings,
  type AiProposalManagerClientSettings,
  type AiProposalManagerConversionTarget,
  type AiProposalManagerDeliveryChannel,
  type AiProposalManagerSourceType,
} from '@/lib/operations/aiProposalManagerConfig';

const DEFAULT_SETTINGS = getAiProposalManagerClientSettings(undefined);

const DELIVERY_CHANNEL_ORDER: AiProposalManagerDeliveryChannel[] = ['email', 'sms', 'whatsapp', 'share_link'];
const CONVERSION_TARGET_ORDER: AiProposalManagerConversionTarget[] = ['quote', 'invoice'];

function buildResponseErrorMessage(
  response: Response,
  data: { error?: string } | null,
  fallback: string,
) {
  return data?.error || `${fallback} (${response.status}${response.statusText ? ` ${response.statusText}` : ''})`;
}

export function useAiProposalManagerSettings() {
  const [settings, setSettings] = useState<AiProposalManagerClientSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = authService.getTokens()?.access_token;

    if (!token) {
      return;
    }

    const loadSettings = async () => {
      try {
        setIsLoading(true);
        const response = await fetchWithRetry(getApiUrl('/api/v1/industry-solutions/config'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }, {
          retries: 2,
        });

        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          throw new Error(buildResponseErrorMessage(response, data, 'Failed to load AI Proposal Manager settings'));
        }

        if (!cancelled) {
          setSettings(getAiProposalManagerClientSettings(data.config?.ai_proposal_manager_settings));
        }
      } catch (error) {
        if (!isSilentFetchError(error)) console.warn('[useAiProposalManagerSettings] Failed to load settings, using defaults:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  const isSourceTypeEnabled = useCallback((sourceType: AiProposalManagerSourceType) => {
    return sourceType === 'site_based'
      ? settings.sourceTypes.siteBased
      : settings.sourceTypes.standalone;
  }, [settings]);

  const isDeliveryChannelEnabled = useCallback((channel: AiProposalManagerDeliveryChannel) => {
    switch (channel) {
      case 'email':
        return settings.deliveryChannels.email;
      case 'sms':
        return settings.deliveryChannels.sms;
      case 'whatsapp':
        return settings.deliveryChannels.whatsapp;
      case 'share_link':
        return settings.deliveryChannels.shareLink;
      default:
        return false;
    }
  }, [settings]);

  const isConversionTargetEnabled = useCallback((target: AiProposalManagerConversionTarget) => {
    return target === 'quote'
      ? settings.conversionTargets.quote
      : settings.conversionTargets.invoice;
  }, [settings]);

  const enabledDeliveryChannels = useMemo(() => {
    return DELIVERY_CHANNEL_ORDER.filter((channel) => isDeliveryChannelEnabled(channel));
  }, [isDeliveryChannelEnabled]);

  const enabledConversionTargets = useMemo(() => {
    return CONVERSION_TARGET_ORDER.filter((target) => isConversionTargetEnabled(target));
  }, [isConversionTargetEnabled]);

  return {
    settings,
    isLoading,
    isSourceTypeEnabled,
    isDeliveryChannelEnabled,
    isConversionTargetEnabled,
    enabledDeliveryChannels,
    enabledConversionTargets,
    hasAnyDeliveryChannelEnabled: enabledDeliveryChannels.length > 0,
    hasAnyConversionTargetEnabled: enabledConversionTargets.length > 0,
    referenceUploadsEnabled: settings.referenceDocuments.uploads,
    referenceParsingEnabled: settings.referenceDocuments.aiParsing,
    referenceExportsEnabled: settings.referenceDocuments.includeInExports,
  };
}
